import { FC, useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  Link,
  TextField,
  Typography,
} from "@mui/material";
import { useResponsiveContext } from "@context/ResponsiveContext";
import DurationTextField from "../controls/DurationTextField";
import { useGameDataContext } from "@context/GameDataContext";
import {
  getImageUrl,
  getPlaybackUrl,
  parseYoutubeId,
} from "@/utils/youtube-utils";

const YoutubeInput: FC<{ open: boolean; onClose: () => void }> = ({
  open,
  onClose,
}) => {
  const { onMobile } = useResponsiveContext();
  const { game, updateYoutubeId, updateVideoOffset } = useGameDataContext();

  const [youtubeId, setYoutubeId] = useState<string>(
    game?.gameData.youtubeId || "",
  );
  const [timestampFirstPoint, setTimestampFirstPoint] = useState<number>(
    game?.gameData.gameStartVideoOffset || 0,
  );
  const [textFieldValue, setTextFieldValue] = useState(
    game?.gameData.youtubeId || "",
  );

  useEffect(() => {
    if (!textFieldValue) {
      setTextFieldValue(youtubeId);
    }
  }, [youtubeId]);

  const onSave = () => {
    if (youtubeId && timestampFirstPoint) {
      updateYoutubeId(youtubeId);
      updateVideoOffset(timestampFirstPoint);
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      fullWidth={true}
      fullScreen={onMobile}
      maxWidth={"sm"}
      keepMounted
      onClose={onClose}
    >
      <Box
        sx={{ p: "2vw", display: "flex", flexDirection: "column", gap: "2vw" }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "2fr 1fr",
            gap: "1vw",
            alignItems: "center",
          }}
        >
          <TextField
            id="outlined-basic"
            label="Youtube Link"
            variant="outlined"
            onChange={(event) => {
              const value = event.target.value;
              setTextFieldValue(value);
              const id = parseYoutubeId(value);
              if (id) {
                setYoutubeId(id);
              } else {
                setYoutubeId("");
              }
              const params = new URLSearchParams(value);
              if (params.has("t")) {
                const t = params.get("t") || "0";
                const seconds = parseInt(t.replace("s", "")) || 0;
                setTimestampFirstPoint(seconds * 1000);
              }
            }}
            value={textFieldValue}
          />

          {youtubeId && (
            <img
              src={getImageUrl(youtubeId)}
              alt="Youtube Thumbnail"
              height={100}
            />
          )}
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            gap: "1vw",
            alignItems: "center",
          }}
        >
          <DurationTextField
            initialValueInMillis={timestampFirstPoint || 0}
            onTimestampUpdate={(newTimestamp) => {
              setTimestampFirstPoint(newTimestamp);
            }}
            label={"Zeitstempel 1. Punkt"}
          />
          {!!timestampFirstPoint && youtubeId && (
            <Link
              href={getPlaybackUrl(youtubeId, timestampFirstPoint)}
              target="_blank"
              rel="noopener"
            >
              Link zum ersten Punkt
            </Link>
          )}
        </Box>

        <Typography variant="body2" color="textSecondary">
          Intuitiv würde man den Anpfiff nehmen, um den Zeitstempel zu setzen.
          Jedoch gibt es in SAMS kein "Anpfiff" Event und das "Satz starten"
          Event kommt deutlich davor, da sonst die Liberos nicht eingetragen
          werden können.
        </Typography>

        <Typography variant="body2" color="textSecondary">
          Daher den ersten Abpfiff nehmen und den Zeitstempel hier eintragen
          (manchmal sieht man auch das "Umspringen" des Spielstands im Stream,
          genau dann).
        </Typography>

        <Typography variant="body2" color="textSecondary">
          Wenn neben dem Feld für den Zeitstempel ein Link eingeblendet wird,
          dann sind alle Angaben valide.
        </Typography>
      </Box>

      <DialogActions>
        <Button onClick={() => onClose()}>Abbrechen</Button>
        <Button
          disabled={!timestampFirstPoint || (!youtubeId && !!textFieldValue)}
          onClick={() => onSave()}
        >
          Speichern
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default YoutubeInput;
